'use client';

import { kebabCase } from 'change-case';
import React, { type HTMLAttributes, useId } from 'react';

import InputError from './InputError';
import InputLabel from './InputLabel';
import TextInput from './TextInput';
import { type TooltipProps } from './Tooltip';
import { Field } from '~/components/ui/field';
import { cn } from '~/lib/utils';
import {
  Select as SelectRoot,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

export type SelectOption<T extends string | number> = {
  value: T | '';
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  meta?: Record<string, string>;
  tooltip?: string;
};

type SelectProps<T extends string | number> = {
  label?: React.ReactNode;
  firstOption?: string;
  options: Array<SelectOption<T>>;
  value?: T | '';
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  readOnly?: boolean;
  tooltip?: React.ReactNode;
  tooltipProps?: Partial<TooltipProps>;
  disabled?: boolean;
  selectInputProps?: Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'disabled'>;
  error?: string;
  placeholder?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>;

const EMPTY_VALUE = '__protege_select_empty__';

function emitChange<T extends string | number>(
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
  next: string,
  options: Array<SelectOption<T>>,
) {
  const selectedIndex = options.findIndex((o) => String(o.value) === next);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  onChange({
    target: {
      value: next,
      selectedIndex,
      options: {
        [selectedIndex]: {
          getAttribute: (name: string) => {
            if (!selected?.meta) return null;
            const key = name.startsWith('data-') ? name.slice(5) : name;
            return selected.meta[key] ?? null;
          },
        },
      },
    },
  } as React.ChangeEvent<HTMLSelectElement>);
}

export default function Select<T extends string | number>(props: SelectProps<T>) {
  const {
    label,
    firstOption,
    options,
    value,
    onChange,
    className,
    readOnly,
    tooltip,
    error,
    disabled,
    selectInputProps,
    tooltipProps,
    placeholder,
    ...others
  } = props;
  const selectId = useId();

  if (readOnly) {
    return (
      <TextInput
        value={options.find((o) => o.value === value)?.label || firstOption || ''}
        tooltip={tooltip}
        label={label}
        tooltipProps={tooltipProps}
        readOnly={true}
        disabled={disabled}
        size="md"
        className={className}
      />
    );
  }

  const stringValue = value === '' || value == null ? undefined : String(value);
  const hasEmptyOption = typeof firstOption === 'string';
  const selectValue = stringValue ?? (hasEmptyOption ? EMPTY_VALUE : undefined);
  const { className: triggerClassName, ...triggerProps } = selectInputProps ?? {};

  return (
    <Field className={cn('font-medium', className)} {...others}>
      {label ? (
        <InputLabel label={label} tooltip={tooltip} tooltipProps={tooltipProps} htmlFor={selectId} />
      ) : null}
      <SelectRoot
        value={selectValue}
        onValueChange={(next) => {
          emitChange(onChange, next === EMPTY_VALUE ? '' : next, options);
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={selectId}
          onClick={(e) => e.stopPropagation()}
          className={cn('font-normal', triggerClassName)}
          {...(triggerProps as React.ComponentProps<typeof SelectTrigger>)}
        >
          <SelectValue placeholder={placeholder || firstOption || undefined} />
        </SelectTrigger>
        <SelectContent position="popper" onClick={(e) => e.stopPropagation()}>
          <SelectGroup>
            {hasEmptyOption ? (
              <SelectItem value={EMPTY_VALUE}>
                <span className="text-muted-foreground">{firstOption || 'Select'}</span>
              </SelectItem>
            ) : null}
            {options.map((o, i) => {
              if (o.value === '') return null;
              const itemValue = String(o.value);
              const dataAttrs = Object.entries(o.meta || {}).reduce<Record<string, string>>(
                (acc, [k, v]) => {
                  acc[`data-${kebabCase(k)}`] = v;
                  return acc;
                },
                {},
              );
              return (
                <SelectItem
                  key={`${itemValue}-${i}`}
                  value={itemValue}
                  disabled={o.disabled}
                  title={o.tooltip}
                  {...dataAttrs}
                >
                  {o.icon ? (
                    <span className="flex items-center gap-2">
                      <span className="inline-flex shrink-0">{o.icon}</span>
                      <span>{o.label}</span>
                    </span>
                  ) : (
                    o.label
                  )}
                </SelectItem>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </SelectRoot>
      {error ? <InputError error={error} /> : null}
    </Field>
  );
}
